// [S-416] Universal inline search — the top-bar field. Type directly in the bar;
// grouped results drop down beneath it: Go to (navigation), Certified templates,
// Precedents, Drafts, Activity. Instant groups render from the TanStack
// cache; the precedent group is the one network call (debounced). ⌘K focuses the
// bar. ↑/↓ across groups, Enter opens, Esc/outside-click closes.
//
// The result engine (data hooks) is mounted only while the dropdown is open, so
// nothing fetches until the user actually searches.
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import {
  useCertifiedTemplates,
  useTenantCertifiedTemplates,
  type CertifiedLibraryFilter,
  type CertifiedTemplateRow,
} from "../data/templatesQuery";
import { useDrafts } from "../data/draftsQuery";
import { useRecentActivity } from "../data/queries";
import { useSearchPrecedents } from "../data/precedentsQuery";
import { navCommandsFor, commandMatches, matchesWords } from "./commands";

const EMPTY_FILTER: CertifiedLibraryFilter = {}; // stable ref — it's in the query key
const PER_GROUP = 5;
const PRECEDENT_MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

export type SearchItem = { key: string; label: string; sub?: string; to: string };

export function InlineSearch(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [items, setItems] = useState<SearchItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const q = query.trim();
  const showDropdown = open && q.length > 0;

  // ⌘K / Ctrl+K focuses the search bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // Keep the highlight in range as results change.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, items.length - 1)));
  }, [items.length]);

  const activate = (item: SearchItem): void => {
    navigate(item.to);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      const it = items[active];
      if (it !== undefined) {
        e.preventDefault();
        activate(it);
      }
    }
  };

  return (
    <div className="tb__search" role="search" ref={rootRef}>
      <SearchIcon />
      <input
        ref={inputRef}
        className="tb__search__input"
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="tb-search-list"
        aria-activedescendant={
          showDropdown && items.length > 0 ? `tb-search-opt-${active}` : undefined
        }
        placeholder="Search precedents, templates, activity…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-testid="tb-search-input"
      />
      <span className="kbd" aria-hidden="true">
        ⌘K
      </span>
      {showDropdown && (
        <SearchResults
          query={q}
          active={active}
          onItems={setItems}
          onSelect={activate}
          onHover={setActive}
        />
      )}
    </div>
  );
}

function SearchResults({
  query,
  active,
  onItems,
  onSelect,
  onHover,
}: {
  query: string;
  active: number;
  onItems: (items: SearchItem[]) => void;
  onSelect: (item: SearchItem) => void;
  onHover: (idx: number) => void;
}): JSX.Element {
  const session = useSession();
  const studioId = session?.studioId ?? null;
  const tenantId =
    studioId !== null && studioId !== undefined ? `tenant-${studioId}` : "";

  const l1 = useCertifiedTemplates(EMPTY_FILTER);
  const l2 = useTenantCertifiedTemplates(tenantId, EMPTY_FILTER);
  const drafts = useDrafts();
  const activity = useRecentActivity(25);
  const precedents = useSearchPrecedents();

  // Debounced precedent search — the only network call the query drives.
  useEffect(() => {
    if (query.length < PRECEDENT_MIN_CHARS || tenantId.length === 0) return undefined;
    const t = setTimeout(() => {
      precedents.mutate({ queryText: query, tenantId });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, tenantId]);

  const navItems = useMemo<SearchItem[]>(
    () =>
      navCommandsFor(session)
        .filter((c) => commandMatches(c, query))
        .slice(0, PER_GROUP)
        .map((c) => ({ key: `nav:${c.id}`, label: c.label, sub: "Go to", to: c.to })),
    [session, query]
  );

  const templateItems = useMemo<SearchItem[]>(() => {
    return [...(l1.data ?? []), ...(l2.data ?? [])]
      .filter((t) => {
        // Match the human label (title, or guild/dealType/tier when title is
        // null) PLUS the full guild name so "director" finds DGA templates.
        const hay = `${t.title ?? ""} ${templateLabel(t)} ${GUILD_FULL[t.guild] ?? ""} ${t.dealType} ${t.tier}`;
        return matchesWords(hay, query);
      })
      .slice(0, PER_GROUP)
      .map((t) => ({
        key: `tpl:${t.id}`,
        label: templateLabel(t),
        sub:
          t.title !== undefined && t.title !== null && t.title.trim().length > 0
            ? `${prettyGuild(t.guild)} · ${prettyDeal(t.dealType)}`
            : "Certified template",
        to: `/library/${encodeURIComponent(t.id)}`,
      }));
  }, [l1.data, l2.data, query]);

  const precedentItems = useMemo<SearchItem[]>(
    () =>
      (precedents.data?.precedents ?? []).slice(0, PER_GROUP).map((p) => ({
        key: `prec:${p.documentId}`,
        // [S-636] Search now returns the rich shape — prefer the parties (deal
        // identity), then the summary, then the id.
        label:
          p.parties.length > 0
            ? p.parties.join(" ⇄ ")
            : p.summary.length > 0
              ? p.summary
              : p.documentId,
        sub: `Precedent${p.dealType !== null ? ` · ${prettyDeal(p.dealType)}` : ""}`,
        to: `/precedent/${encodeURIComponent(p.documentId)}`,
      })),
    [precedents.data]
  );

  const draftItems = useMemo<SearchItem[]>(() => {
    return (drafts.data ?? [])
      .filter((d) => matchesWords(d.title, query))
      .slice(0, PER_GROUP)
      .map((d) => ({
        key: `draft:${d.id}`,
        label: d.title.length > 0 ? d.title : "Untitled draft",
        sub: "Draft",
        to: `/drafts/${encodeURIComponent(d.id)}`,
      }));
  }, [drafts.data, query]);

  const activityItems = useMemo<SearchItem[]>(() => {
    return (activity.data ?? [])
      .filter((r) => matchesWords(r.title, query))
      .slice(0, PER_GROUP)
      .map((r) => ({
        key: `act:${r.id}`,
        label: r.title,
        sub: `Activity · ${r.actor.displayName}`,
        to: "/activity",
      }));
  }, [activity.data, query]);

  const flat = useMemo(
    () => [...navItems, ...templateItems, ...precedentItems, ...draftItems, ...activityItems],
    [navItems, templateItems, precedentItems, draftItems, activityItems]
  );

  // Report the flat list up so the input can drive keyboard nav + Enter.
  useEffect(() => {
    onItems(flat);
  }, [flat, onItems]);

  const precedentStatus = precedentStatusText({
    q: query,
    // "No tenant scope to search" — platform staff have studioId null; this is
    // a display-status signal, not a role gate.
    isPlatform: studioId === null,
    hasTenant: tenantId.length > 0,
    pending: precedents.isPending,
    error: precedents.isError,
    count: precedentItems.length,
  });

  let idx = 0;
  const rows = (group: SearchItem[]): JSX.Element[] =>
    group.map((it) => {
      const myIdx = idx++;
      return (
        <li
          key={it.key}
          id={`tb-search-opt-${myIdx}`}
          role="option"
          aria-selected={myIdx === active}
          className={myIdx === active ? "tbs__row is-active" : "tbs__row"}
          onMouseMove={() => onHover(myIdx)}
          onMouseDown={(e) => {
            // mousedown (not click) so the outside-click handler doesn't fire
            // first and unmount this row before the click lands.
            e.preventDefault();
            onSelect(it);
          }}
          data-testid="tb-search-row"
        >
          <span className="tbs__row__label">{it.label}</span>
          {it.sub !== undefined && <span className="tbs__row__sub">{it.sub}</span>}
        </li>
      );
    });

  const noResults = flat.length === 0 && precedentStatus === null;

  return (
    <ul className="tbs__list" id="tb-search-list" role="listbox" data-testid="tb-search-list">
      {navItems.length > 0 && <GroupLabel>Go to</GroupLabel>}
      {rows(navItems)}
      {templateItems.length > 0 && <GroupLabel>Certified templates</GroupLabel>}
      {rows(templateItems)}
      {(precedentItems.length > 0 || precedentStatus !== null) && (
        <GroupLabel>Precedents</GroupLabel>
      )}
      {rows(precedentItems)}
      {precedentStatus !== null && (
        <li className="tbs__status" role="presentation" data-testid="tb-search-prec-status">
          {precedentStatus}
        </li>
      )}
      {draftItems.length > 0 && <GroupLabel>Drafts</GroupLabel>}
      {rows(draftItems)}
      {activityItems.length > 0 && <GroupLabel>Activity</GroupLabel>}
      {rows(activityItems)}
      {noResults && (
        <li className="tbs__status" role="presentation">
          No matches for “{query}”.
        </li>
      )}
    </ul>
  );
}

function GroupLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <li className="tbs__group" role="presentation" aria-hidden="true">
      {children}
    </li>
  );
}

function prettyDeal(dealType: string): string {
  const s = dealType.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Full guild names so queries like "director" / "writer" find DGA / WGA
// templates whose label only carries the acronym.
const GUILD_FULL: Record<string, string> = {
  SAG: "SAG-AFTRA Screen Actors Guild",
  DGA: "Directors Guild of America",
  WGA: "Writers Guild of America",
  IATSE: "International Alliance of Theatrical Stage Employees",
  AFM: "American Federation of Musicians",
  PGA: "Producers Guild of America",
};

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

// Mirrors TemplateDetail.prettyTitle: the persisted title wins; legacy rows
// (title null) fall back to a guild/dealType/tier label — never the raw id.
function templateLabel(t: CertifiedTemplateRow): string {
  if (typeof t.title === "string" && t.title.trim().length > 0) return t.title;
  const tier = t.tier.charAt(0).toUpperCase() + t.tier.slice(1);
  return `${prettyGuild(t.guild)} · ${prettyDeal(t.dealType)} · ${tier}`;
}

function precedentStatusText(args: {
  q: string;
  isPlatform: boolean;
  hasTenant: boolean;
  pending: boolean;
  error: boolean;
  count: number;
}): string | null {
  if (args.q.length < PRECEDENT_MIN_CHARS) return null;
  if (args.isPlatform || !args.hasTenant) return "Pick an organization to search precedents.";
  if (args.pending) return "Searching precedents…";
  if (args.error) return "Couldn't search precedents.";
  if (args.count === 0) return "No precedents match.";
  return null;
}

function SearchIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
